/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Fragment } from 'react';

import { AccordionGroup, VisuallyHidden } from '@coursera/cds-core';

import type { Item } from 'bundles/learner-progress/types/Item';
import { type Track } from 'bundles/naptimejs/resources/programCurriculums.v1';
import SkillProgressDetailedListItem from 'bundles/skills-common/shared/verified-skills/components/SkillProgressDetailedListItem';
import type { RenderRetryActionButton } from 'bundles/skills-common/shared/verified-skills/components/SkillsDialog';
import {
  SkillAreasThresholdRequestTrackerProvider,
  useSkillAreasThresholdRequestTracker,
} from 'bundles/skills-common/shared/verified-skills/context/SkillAreasThresholdRequestTracker';
import type { VerifiedSkillsEnterpriseBadgeCollectionFragment } from 'bundles/skills-common/shared/verified-skills/data/pathway-badge/__generated__/VerifiedSkillsEnterpriseBadgeCollectionsQuery';
import type { VerifiedSkillsEnterpriseProgramsV1Fragment } from 'bundles/skills-common/shared/verified-skills/data/pathway-badge/__generated__/VerifiedSkillsEnterpriseProgramForCourseQuery';
import type { SkillAttainmentSkillAreaAttainmentFragment } from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/__generated__/GetSkillAttainmentByOccupationGroupAndCareerBandQuery';
import type { SkillPathwayCurriculumProductsFragment } from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/__generated__/GetSkillPathwayCurriculumProductsQuery';
import useGetSkillAreasWithUserData from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/useGetSkillAreasWithUserData';
import type { TrackingData } from 'bundles/skills-common/shared/verified-skills/types/TrackingData';

import _t from 'i18n!nls/skills-common';

type Props = {
  skillAreaAttainments: SkillAttainmentSkillAreaAttainmentFragment[];
  focusedSkillAreaId?: string;

  // props to handle retrying assessment when entering from a skill assessment results page
  item?: Item;
  trackingData?: TrackingData;

  renderRetryActionButton?: RenderRetryActionButton;

  // enrollment props for enrol cards
  enrollmentProps?: {
    programId: string;
    pathwayCollectionId?: string;
    programSlug?: string;
    thirdPartyOrganizationSlug?: string;
  };
  skillPathCurriculumProducts?: (SkillPathwayCurriculumProductsFragment | null)[];
  program?: VerifiedSkillsEnterpriseProgramsV1Fragment | null;
  enrolledBadgeCollection?: VerifiedSkillsEnterpriseBadgeCollectionFragment | null;
  skillPath?: Track;
  handleDialogOnClose?: () => void;
};

const styles = {
  accordionGroup: css`
    .cds-AccordionGroup-itemSpacing {
      margin-top: 0 !important;
    }
  `,
};

type SkillProgressDetailedListContentProps = {
  children: React.ReactNode;
};

const SkillProgressDetailedListContent = ({ children }: SkillProgressDetailedListContentProps) => {
  const { showProgressBars } = useSkillAreasThresholdRequestTracker();

  return (
    <Fragment>
      <VisuallyHidden role="status" aria-live="polite" aria-atomic="true" data-testid="skill-progress-status">
        {showProgressBars ? _t('Skill progress loaded') : _t('Loading skill progress')}
      </VisuallyHidden>
      <div aria-busy={!showProgressBars}>{children}</div>
    </Fragment>
  );
};

const SkillProgressDetailedList = ({
  skillAreaAttainments,
  focusedSkillAreaId,
  item,
  trackingData,
  renderRetryActionButton,
  enrollmentProps,
  skillPathCurriculumProducts,
  program,
  enrolledBadgeCollection,
  skillPath,
  handleDialogOnClose,
}: Props) => {
  const { loading: skillAreasWithUserDataLoading } = useGetSkillAreasWithUserData({
    programId: enrollmentProps?.programId || program?.id || '',
  });

  if (skillAreaAttainments?.length === 0) {
    return null;
  }

  return (
    <AccordionGroup spacing={8} css={styles.accordionGroup}>
      <SkillAreasThresholdRequestTrackerProvider>
        <SkillProgressDetailedListContent>
          {skillAreaAttainments.map((skillAreaAttainment) => (
            <SkillProgressDetailedListItem
              key={skillAreaAttainment.skillArea.id}
              skillAreaAttainment={skillAreaAttainment}
              focusedSkillAreaId={focusedSkillAreaId}
              skillPathCurriculumProducts={skillPathCurriculumProducts}
              program={program}
              enrolledBadgeCollection={enrolledBadgeCollection}
              skillPath={skillPath}
              item={item}
              trackingData={trackingData}
              renderRetryActionButton={renderRetryActionButton}
              enrollmentProps={enrollmentProps}
              skillAreasWithUserDataLoading={skillAreasWithUserDataLoading}
              handleDialogOnClose={handleDialogOnClose}
            />
          ))}
        </SkillProgressDetailedListContent>
      </SkillAreasThresholdRequestTrackerProvider>
    </AccordionGroup>
  );
};

export default SkillProgressDetailedList;
